import { defineStore } from 'pinia'
import type {
  DBWSCreate,
  DBWSUpdate,
  DemoAgreements,
  DemoContact,
  DemoContactUpdate,
  DemoPayments, DirectDepositDefaultUpdate,
  DirectDepositUpdate,
  PPCustomersList,
  TableQueryPayload
} from '~/types/demo'
import type { Demonstrator } from '~/types/account'
import { filterTableUrlParams, processQueryFilter } from '~/utils'

export const useDemoStore = defineStore('demo', () => {
  const contact = ref<DemoContact>()
  const demoPayments = ref<DemoPayments>()
  const ppCustomers = ref<PPCustomersList>()
  const agreements = ref<DemoAgreements>()

  const downloadEmailSubsCsv = async () => {
    const content = await $api()<string>('account/demo/customersubs/csv', { responseType: 'blob' })
    const fileSaver = await import('file-saver')
    fileSaver.saveAs(content, 'email_subscribers.csv')
  }

  const fetchPpSubs = async (query: TableQueryPayload) => {
    const params = filterTableUrlParams(processQueryFilter(query))
    const customers: PPCustomersList = await $api()('account/demo/ppsubs', { params })
    ppCustomers.value = customers
    return customers
  }

  const downloadPpSubsCsv = async () => {
    const content = await $api()<string>('account/demo/ppsubs/csv', { responseType: 'blob' })
    const fileSaver = await import('file-saver')
    fileSaver.saveAs(content, 'paperpumpkin_customers.csv')
  }

  const fetchAgreements = async () => {
    const response = await $api()<DemoAgreements>('account/demo/legal/')
    agreements.value = response
    return response
  }

  const updateAgreements = async (agreementUpdate: DemoAgreements) => {
    const response = await $api()<DemoAgreements>('account/demo/legal/', { method: 'PATCH', body: agreementUpdate })
    agreements.value = response
    return response
  }

  // currentPassword: see useCurrentPasswordOnEmailChange (AB#65190)
  const updateContact = async (contactUpdate: DemoContact | DemoContactUpdate, currentPassword?: string) => {
    const body = currentPassword ? { ...contactUpdate, currentPassword } : contactUpdate
    const contactLocal: DemoContact = await $api()<DemoContact>('account/demo/settings', { body, method: 'PATCH' })
    contact.value = contactLocal
    return contactLocal
  }

  const fetchContact = async () => {
    const contactLocal = await $api()<DemoContact>('account/demo/settings')
    contact.value = contactLocal
    return contactLocal
  }

  const fetchDemonstrator = async (id: number) => {
    const demonstrator = await $api()<Demonstrator>(`demonstrator/${id}`)
    return demonstrator
  }

  const createDBWSPayment = async (dbwsCreate: DBWSCreate) => {
    await $api()<Demonstrator>('account/demo/payment/dbws-account', { body: dbwsCreate, method: 'POST' })
    demoPayments.value = { ...demoPayments.value, hasDbws: true, dbwsPaymentAccountId: dbwsCreate?.paymentAccountId ?? 0 } as DemoPayments
  }

  const updateDBWSPayment = async (dbwsUpdate: DBWSUpdate) => {
    await $api()<Demonstrator>('account/demo/payment/dbws-account', { body: dbwsUpdate, method: 'PATCH' })
    demoPayments.value = { ...demoPayments.value, hasDbws: !dbwsUpdate?.cancel, dbwsPaymentAccountId: dbwsUpdate?.paymentAccountId ?? 0 } as DemoPayments
  }

  const deleteEftPayment = async (paymentAccountId: number) => {
    await $api()(`account/demo/payment/eft/${paymentAccountId}`, { method: 'DELETE' })
  }

  const createEftPayment = async (deposit: DirectDepositUpdate) => {
    await $api()('account/demo/payment/eft', { method: 'POST', body: deposit })
  }

  const updateDeposit = async (deposit: DirectDepositUpdate) => {
    await $api()('account/demo/payment/direct-deposit', { method: 'POST', body: deposit })
  }

  const makeEftDefaultPayment = async (deposit: DirectDepositDefaultUpdate) => {
    await $api()(`account/demo/payment/eft/${deposit.paymentAccountId}`, { method: 'PUT', body: { isDefaultPayment: deposit.isDefaultPayment } })
  }

  const fetchPaymentList = async (isSubscription: boolean = false) => {
    const params = isSubscription ? { isSubscription: true } : {}
    const payments = await $api()<DemoPayments>('account/demo/payment', { params })
    demoPayments.value = payments
    return payments
  }

  const updateDefaultCulture = async (newCulture: string) => {
    return await $api()(`account/demo/switch-culture/${newCulture}`, { method: 'POST' })
  }

  const directDeposit = computed(() => demoPayments.value?.directDepositAccount)
  const paymentById = (paymentId: string) => computed(() => demoPayments.value?.creditCards.find(p => p.billingInfoId === paymentId))
  const paymentEftById = (paymentAccountId: number) => computed(() => demoPayments.value?.eftAccounts.find(p => p.paymentAccountId === paymentAccountId))

  return {
    contact,
    demoPayments,
    ppCustomers,
    agreements,
    paymentEftById,
    paymentById,
    directDeposit,
    updateContact,
    fetchDemonstrator,
    downloadEmailSubsCsv,
    fetchPpSubs,
    downloadPpSubsCsv,
    fetchAgreements,
    updateAgreements,
    fetchPaymentList,
    updateDBWSPayment,
    updateDeposit,
    createEftPayment,
    makeEftDefaultPayment,
    deleteEftPayment,
    createDBWSPayment,
    fetchContact,
    updateDefaultCulture
  }
})
